import React, { useState, useRef, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Edit2, Check, X, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'

interface EditableChartTitleProps {
  title: string
  onSave: (newTitle: string) => Promise<void>
  className?: string
  textClassName?: string
  inputClassName?: string
  disabled?: boolean
  isLoading?: boolean
}

const EditableChartTitle: React.FC<EditableChartTitleProps> = ({
  title,
  onSave,
  className = '',
  textClassName = 'text-lg font-semibold text-foreground',
  inputClassName = 'text-lg font-semibold',
  disabled = false,
  isLoading: externalIsLoading = false,
}) => {
  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState(title)
  const [isSaving, setIsSaving] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    setEditValue(title)
  }, [title])

  useEffect(() => {
    if (isEditing && inputRef.current) {
      requestAnimationFrame(() => {
        if (inputRef.current) {
          inputRef.current.focus()
          inputRef.current.select()
        }
      })
    }
  }, [isEditing])

  const handleStartEdit = (e?: React.MouseEvent | React.KeyboardEvent) => {
    if (e) e.stopPropagation()
    if (disabled || externalIsLoading) return
    setIsEditing(true)
    setEditValue(title)
  }

  const handleSave = async () => {
    if (editValue.trim() === '' || editValue.trim() === title) {
      handleCancel()
      return
    }
    setIsSaving(true)
    try {
      await onSave(editValue.trim())
      setIsEditing(false)
    } catch (error) {
      console.error('Error saving chart title:', error)
    } finally {
      setIsSaving(false)
    }
  }

  const handleCancel = () => {
    setIsEditing(false)
    setEditValue(title)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSave()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      handleCancel()
    }
  }
  
  const actualIsSaving = isSaving || externalIsLoading

  if (isEditing) {
    return (
      <div className={cn("flex items-center gap-2 w-full", className)}>
        <Input
          ref={inputRef}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          className={cn("flex-1", inputClassName)}
          disabled={actualIsSaving}
          placeholder="Enter title..."
          autoFocus
          onFocus={(e) => e.target.select()}
        />
        <Button 
            size="icon" 
            onClick={handleSave} 
            disabled={actualIsSaving || !editValue.trim()} 
            className="h-8 w-8 p-0 flex-shrink-0"
            aria-label={actualIsSaving ? "Saving title" : "Save title"}
        >
          {actualIsSaving ? <Loader2 className="h-4 w-4 animate-spin" data-testid="loader-icon" /> : <Check className="h-4 w-4" data-testid="check-icon" />}
        </Button>
        <Button 
            size="icon" 
            variant="ghost" 
            onClick={handleCancel} 
            disabled={actualIsSaving} 
            className="h-8 w-8 p-0 flex-shrink-0"
            aria-label="Cancel edit title"
        >
          <X className="h-4 w-4" data-testid="cancel-icon" />
        </Button>
      </div>
    )
  }

  return (
    <div 
      className={cn("flex items-center gap-2 group min-w-0", className, {"cursor-text": !disabled})}
      onClick={!disabled ? handleStartEdit : undefined} 
      onKeyDown={(e) => { if (!disabled && (e.key === 'Enter' || e.key === ' ')) handleStartEdit(e);}}
      role={!disabled ? "button" : undefined}
      tabIndex={!disabled ? 0 : undefined}
      title={!disabled ? "Edit title" : title}
    >
      <h2 className={cn("truncate", textClassName)}>{title || "Untitled"}</h2>
      {!disabled && !actualIsSaving && (
        <Edit2 className="h-4 w-4 ml-1 text-muted-foreground opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity flex-shrink-0" data-testid="edit-icon" />
      )}
       {actualIsSaving && !isEditing && <Loader2 className="h-4 w-4 ml-1 animate-spin text-muted-foreground flex-shrink-0" data-testid="display-loader-icon"/>}
    </div>
  )
}

export default EditableChartTitle 